import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../components/common/SafeIcon';
import supabase from '../../supabase/supabase';
import { useAuth } from '../../context/AuthContext';

const { FiShield, FiCheck, FiAlertCircle, FiRefreshCw, FiLogIn } = FiIcons;

export default function SetupAdmin() {
  const navigate = useNavigate();
  const { user, profile, loading, profileLoading, refetchProfile } = useAuth();

  const [promoting, setPromoting]       = useState(false);
  const [promoted, setPromoted]         = useState(false);
  const [error, setError]               = useState('');
  const [adminExists, setAdminExists]   = useState(null); // null = unchecked
  const [checkingAdmin, setCheckingAdmin] = useState(true);

  const attemptsRef       = useRef(0);
  const maxAttempts       = 40; // 40 × 500 ms = 20 s
  const pollingIntervalRef = useRef(null);
  const pollingActiveRef  = useRef(false);

  // ── Check if a platform admin already exists ─────────────────
  useEffect(() => {
    if (loading || profileLoading) return;

    async function checkAdminExists() {
      try {
        // Use count query — works even for anon users if RLS allows SELECT
        const { count, error: countErr } = await supabase
          .from('d2f_profiles')
          .select('id', { count: 'exact', head: true })
          .eq('role', 'platform_admin');

        if (countErr) throw countErr;

        const exists = (count ?? 0) > 0;
        setAdminExists(exists);

        if (exists && profile?.role === 'platform_admin') {
          navigate('/admin', { replace: true });
        }
      } catch (err) {
        console.error('[SetupAdmin] checkAdminExists error:', err);
        // Assume no admin so the page stays usable
        setAdminExists(false);
      } finally {
        setCheckingAdmin(false);
      }
    }

    checkAdminExists();
  }, [loading, profileLoading, navigate, profile?.role]);

  // ── Poll for profile promotion after RPC succeeds ────────────
  useEffect(() => {
    if (!promoted) return;
    if (!user?.id) return;

    const pollProfile = async () => {
      if (pollingActiveRef.current) return;
      pollingActiveRef.current = true;

      try {
        const { data, error: pollErr } = await supabase
          .from('d2f_profiles')
          .select('role')
          .eq('id', user.id)
          .single();

        if (pollErr) throw pollErr;

        if (data?.role === 'platform_admin') {
          clearInterval(pollingIntervalRef.current);
          await supabase.auth.refreshSession();
          await refetchProfile();
          navigate('/admin', { replace: true });
          return;
        }

        attemptsRef.current += 1;
        if (attemptsRef.current >= maxAttempts) {
          clearInterval(pollingIntervalRef.current);
          setError('Role assigned but profile did not sync in time. Please refresh the page and sign in again.');
        }
      } catch (err) {
        console.error('[SetupAdmin] poll error:', err);
        attemptsRef.current += 1;
        if (attemptsRef.current >= maxAttempts) {
          clearInterval(pollingIntervalRef.current);
          setError('Failed to verify promotion. Please try refreshing the page.');
        }
      } finally {
        pollingActiveRef.current = false;
      }
    };

    attemptsRef.current = 0;
    pollProfile();
    pollingIntervalRef.current = setInterval(pollProfile, 500);

    return () => clearInterval(pollingIntervalRef.current);
  }, [promoted, user?.id, navigate, refetchProfile]);

  const handlePromote = async () => {
    if (!user?.email) return;
    setPromoting(true);
    setError('');

    try {
      const { data, error: rpcErr } = await supabase
        .rpc('assign_platform_admin', { target_email: user.email });

      if (rpcErr) throw rpcErr;

      if (data === 'ok') {
        setPromoted(true);
      } else if (data === 'unauthorized') {
        setError('A platform admin already exists. You cannot self-promote.');
      } else if (data === 'user_not_found') {
        setError('Your account was not found. Please sign out and sign in again.');
      } else {
        setError('Unexpected server response: ' + data);
      }
    } catch (err) {
      console.error('[SetupAdmin] handlePromote error:', err);
      setError(err.message || 'Failed to promote. Please try again.');
    } finally {
      setPromoting(false);
    }
  };

  const progressPct = Math.min((attemptsRef.current / maxAttempts) * 100, 100);

  // ── Loading state ────────────────────────────────────────────
  if (loading || profileLoading || checkingAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <SafeIcon icon={FiRefreshCw} className="w-12 h-12 text-purple-400 mx-auto mb-4 animate-spin" />
          <p className="text-white text-lg">Checking admin status…</p>
        </div>
      </div>
    );
  }

  // ── Already an admin ─────────────────────────────────────────
  if (adminExists && profile?.role === 'platform_admin') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <SafeIcon icon={FiCheck} className="w-16 h-16 text-green-400 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-white mb-2">Admin Access Confirmed</h1>
          <p className="text-gray-300 mb-4">Redirecting to dashboard…</p>
        </div>
      </div>
    );
  }

  // ── Admin exists but caller is not one ───────────────────────
  if (adminExists && profile?.role !== 'platform_admin') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-gray-800 rounded-2xl p-8 border border-gray-700">
          <SafeIcon icon={FiAlertCircle} className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white text-center mb-4">Platform Admin Already Exists</h1>
          <p className="text-gray-300 text-center mb-6">
            A platform admin already exists. Contact them to be granted access.
          </p>
          <button
            onClick={() => navigate('/')}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-gray-800 rounded-2xl p-8 border border-gray-700 shadow-2xl">
        <div className="text-center mb-8">
          <SafeIcon icon={FiShield} className="w-16 h-16 text-purple-400 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-white mb-2">Bootstrap Platform Admin</h1>
          <p className="text-gray-400">Create the first platform administrator account</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-900/30 border border-red-500 rounded-lg flex items-start gap-3">
            <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-red-300 text-sm">{error}</p>
          </div>
        )}

        {/* Promoting / polling state */}
        {promoted ? (
          <div className="text-center space-y-4">
            <SafeIcon icon={FiRefreshCw} className="w-12 h-12 text-purple-400 mx-auto animate-spin" />
            <h2 className="text-xl font-semibold text-white">Verifying Promotion…</h2>
            <p className="text-gray-400 text-sm">Please wait while we confirm your admin access</p>
            <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
              <div
                className="bg-gradient-to-r from-purple-500 to-pink-500 h-3 rounded-full transition-all duration-300"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <p className="text-gray-500 text-xs">Attempt {attemptsRef.current} / {maxAttempts}</p>
          </div>

        ) : user ? (
          /* Signed-in — show promote button */
          <button
            onClick={handlePromote}
            disabled={promoting}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:from-gray-600 disabled:to-gray-600 text-white font-semibold py-4 px-6 rounded-lg transition-all flex items-center justify-center gap-3 disabled:cursor-not-allowed"
          >
            {promoting
              ? <><SafeIcon icon={FiRefreshCw} className="w-5 h-5 animate-spin" /> Promoting…</>
              : <><SafeIcon icon={FiShield} className="w-5 h-5" /> Assign Platform Admin to {user.email}</>
            }
          </button>

        ) : (
          /* Not signed in — prompt to sign in first */
          <div className="space-y-4">
            <div className="p-4 bg-purple-900/30 border border-purple-500/30 rounded-lg text-center">
              <p className="text-purple-200 text-sm">
                You must be signed in to claim the platform admin role.
              </p>
            </div>
            <button
              onClick={() => navigate('/auth', { state: { returnTo: '/setup-admin' } })}
              className="w-full bg-white hover:bg-gray-100 text-gray-900 font-bold py-4 px-6 rounded-lg transition-all flex items-center justify-center gap-2"
            >
              <SafeIcon icon={FiLogIn} className="w-5 h-5" />
              Sign In or Create Account
            </button>
          </div>
        )}

        <div className="mt-6 p-4 bg-gray-700/50 rounded-lg border border-gray-600">
          <h3 className="text-sm font-semibold text-gray-300 mb-2">What this does:</h3>
          <ul className="text-xs text-gray-400 space-y-1">
            <li>• Grants platform_admin role to your account</li>
            <li>• Enables access to the admin dashboard</li>
            <li>• Allows managing users, artists, and labels</li>
            <li>• Only works when no admin exists yet</li>
          </ul>
        </div>
      </div>
    </div>
  );
}